import { AppContext } from 'next/app';
import { SessionInitialState } from '@sls-react/contexts/SessionContext';
import getUuid from '@sls-react/utils/getUuid';
import redirect from '@sls-react/utils/redirect';
import { generateLoginLink } from '@sls-react/components/Links/LoginLink';
import qsParse from '@sls-react/utils/qsParse';
import { NextPageContext } from 'next';
import qsStringify from '@sls-react/utils/qsStringify';

class PageGetInitialPropsFailed extends Error {
  private url: string | undefined;
  private userClientId: string | undefined;
  private decodedUserToken: string | undefined;

  constructor(error: Error, ctx: NextPageContext) {
    super(
      `${error.name}: ${error.message}; pageUrl="${ctx.asPath}" userClientId="${ctx.session.clientId}"`,
    );
    this.name = 'PageGetInitialPropsFailed';

    this.url = ctx.asPath;
    this.userClientId = ctx.session.clientId;
    if (ctx.session?.user) {
      this.decodedUserToken = JSON.stringify(ctx.session.user);
    }
  }
}

export async function performRedirect(
  appCtx: AppContext,
  initialSessionState: SessionInitialState,
) {
  const { ctx, router, Component } = appCtx;
  let shouldRedirect = false;

  if (ctx.req) {
    if (Component['securePage'] && !initialSessionState.loggedIn) {
      shouldRedirect = true;
    }
  } else {
    if (Component['securePage'] && !getUuid(ctx)) {
      shouldRedirect = true;
    }
  }

  if (!ctx.session) {
    // @ts-ignore
    ctx.session = initialSessionState || { loggedIn: false };
    // @ts-ignore
    ctx.session.logout = async () => {
      if (shouldRedirect) {
        const params = {
          destination: router.asPath,
          message: Component['securePageMessage'],
        };
        throw await redirect(ctx, generateLoginLink(params));
      }
    };
  }

  if (shouldRedirect) {
    const params = {
      destination: router.asPath,
      message: Component['securePageMessage'],
    };
    throw await redirect(ctx, generateLoginLink(params));
  }
}

export async function getPageProps(appCtx: AppContext) {
  const { ctx, Component } = appCtx;
  if (Component.getInitialProps) {
    try {
      // Stringify and re-parse to avoid keys that end in []
      const query = qsParse(qsStringify(ctx.query));

      return Component.getInitialProps({
        ...ctx,
        query,
      });
    } catch (e) {
      const error = e as Error;
      if (error.name === 'Redirected') {
        // Don't throw page errors that are actually intentional
        return {};
      } else {
        const err = new PageGetInitialPropsFailed(error, ctx);
        ctx.err = err;
        throw err;
      }
    }
  }

  return {};
}
